import { cajaContenido, MARGEN_SECTOR, RADIO_ASIENTO } from "./geometria";
import { nuevaClave, crearSectorGeneral, nombreUnico, moverTarifa } from "./mapa";

export const FILAS_POR_DEFECTO = {
  filas: 5,
  asientosPorFila: 10,
  separacion: 28,
  separacionFilas: 34,
  curva: 0,
  nombres: "letras",
  primeraFila: "A",
  primerAsiento: 1,
  sentido: "izquierda",
};

export const MAX_FILAS = 50;
export const MAX_ASIENTOS_POR_FILA = 50;
export const MAX_SILLAS_MESA = 40;

const LETRAS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// A, B, … Z, AA, AB, … como las columnas de una planilla
const letraDesde = (n) => {
  let resto = n;
  let texto = "";
  do {
    texto = LETRAS[resto % 26] + texto;
    resto = Math.floor(resto / 26) - 1;
  } while (resto >= 0);
  return texto;
};

const indiceDeLetras = (texto) =>
  texto
    .toUpperCase()
    .split("")
    .reduce((total, letra) => total * 26 + (LETRAS.indexOf(letra) + 1), 0) - 1;

export const nombreFila = (indice, { nombres, primeraFila }) => {
  if (nombres === "numeros") return String((parseInt(primeraFila, 10) || 1) + indice);
  const inicio = /^[A-Za-z]+$/.test(primeraFila || "") ? indiceDeLetras(primeraFila) : 0;
  return letraDesde(inicio + indice);
};

const etiquetaAsiento = (i, cantidad, { primerAsiento, sentido }) => {
  const posicion = sentido === "derecha" ? cantidad - 1 - i : i;
  return String((Number(primerAsiento) || 1) + posicion);
};

// generarFilas arma las filas en coordenadas propias (se acomodan después en
// la caja del sector). Con curva, las filas son arcos con el mismo centro,
// del lado del escenario, como en un teatro.
export const generarFilas = (config) => {
  const c = { ...FILAS_POR_DEFECTO, ...config };
  const filas = [];
  const ancho = (c.asientosPorFila - 1) * c.separacion;
  const angulo = (Math.min(Math.max(c.curva, 0), 100) / 100) * Math.PI * 0.9;
  const radioBase = angulo > 0 ? ancho / angulo : 0;

  for (let f = 0; f < c.filas; f++) {
    // una fila separada a otra ubicación no se genera acá (las demás no se mueven)
    if (c.excluidas?.includes(f)) continue;
    // una fila puede tener su propio nombre y su propia cantidad de asientos
    const propia = c.ajustes?.[f] || {};
    const nombre = propia.nombre || nombreFila(f, c);
    const cantidad = propia.asientos || c.asientosPorFila;
    const asientos = [];
    for (let i = 0; i < cantidad; i++) {
      let x;
      let y;
      let rotacion = 0;
      if (angulo > 0) {
        // una fila con más asientos que el bloque no se curva más que él: usa
        // un radio más abierto (si no, el arco se cierra sobre sí mismo)
        const radio = Math.max(radioBase + f * c.separacionFilas, ((cantidad - 1) * c.separacion) / angulo);
        const paso = cantidad > 1 ? c.separacion / radio : 0;
        const phi = (i - (cantidad - 1) / 2) * paso;
        x = ancho / 2 + radio * Math.sin(phi);
        y = f * c.separacionFilas + radio * (Math.cos(phi) - 1);
        rotacion = Math.round((-phi * 180) / Math.PI);
      } else {
        x = i * c.separacion;
        y = f * c.separacionFilas;
      }
      asientos.push({ etiqueta: etiquetaAsiento(i, cantidad, c), x: Math.round(x), y: Math.round(y), rotacion });
    }
    filas.push(ajustarUnaFila({
      nombre,
      x: 0,
      y: Math.round(f * c.separacionFilas),
      rotacion: 0,
      curva: c.curva,
      separacion: c.separacion,
      asientos,
    }, c.ajustes?.[f]));
  }
  return filas;
};

// ajustarUnaFila aplica lo que se movió o giró a mano una fila: gira sus
// asientos alrededor del centro de la fila y la desplaza.
const ajustarUnaFila = (fila, ajuste) => {
  if (!ajuste || (!ajuste.dx && !ajuste.dy && !ajuste.rotacion)) return fila;
  const { dx = 0, dy = 0, rotacion = 0 } = ajuste;
  const xs = fila.asientos.map((a) => a.x);
  const ys = fila.asientos.map((a) => a.y);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  return {
    ...fila,
    x: fila.x + dx,
    y: fila.y + dy,
    rotacion,
    asientos: fila.asientos.map((a) => {
      const p = rotar(a.x - cx, a.y - cy, rotacion);
      return { ...a, x: Math.round(cx + p.x + dx), y: Math.round(cy + p.y + dy), rotacion: Math.round((a.rotacion || 0) + rotacion) };
    }),
  };
};

const rotar = (dx, dy, grados) => {
  const r = (grados * Math.PI) / 180;
  return { x: dx * Math.cos(r) - dy * Math.sin(r), y: dx * Math.sin(r) + dy * Math.cos(r) };
};

const moverOrigen = (editor, dx, dy) =>
  editor?.filas?.origen
    ? { ...editor, filas: { ...editor.filas, origen: { x: editor.filas.origen.x + dx, y: editor.filas.origen.y + dy } } }
    : editor;

const moverContenido = (sector, dx, dy) => ({
  ...sector,
  ...(sector.editor ? { editor: moverOrigen(sector.editor, dx, dy) } : {}),
  filas: sector.filas.map((f) => ({
    ...f,
    x: f.x + dx,
    y: f.y + dy,
    asientos: f.asientos.map((a) => ({ ...a, x: a.x + dx, y: a.y + dy })),
  })),
  mesas: sector.mesas.map((m) => ({ ...m, x: m.x + dx, y: m.y + dy })),
  decorativos: sector.decorativos.map((d) => ({
    ...d,
    x: d.x + dx,
    y: d.y + dy,
    ...(d.tipo === "linea"
      ? { punto_final_x: (d.punto_final_x ?? d.x) + dx, punto_final_y: (d.punto_final_y ?? d.y) + dy }
      : {}),
  })),
});

// ajustarCaja deja la caja del sector justo alrededor de su contenido, sin
// mover nada en el plano: lo que se corre adentro se compensa en la posición
// del sector (girada, si el sector está rotado).
export const ajustarCaja = (sector) => {
  const caja = cajaContenido(sector);
  if (!caja) return sector;
  const dx = Math.round(MARGEN_SECTOR - caja.minX);
  const dy = Math.round(MARGEN_SECTOR - caja.minY);
  const corrimiento = rotar(-dx, -dy, sector.rotacion || 0);
  return {
    ...moverContenido(sector, dx, dy),
    x: Math.round(sector.x + corrimiento.x),
    y: Math.round(sector.y + corrimiento.y),
    ancho: Math.ceil(caja.maxX - caja.minX + 2 * MARGEN_SECTOR),
    alto: Math.ceil(caja.maxY - caja.minY + 2 * MARGEN_SECTOR),
  };
};

const claveAsiento = (fila, etiqueta) => `${fila}|${etiqueta}`;

// conIdsAnteriores le pasa a cada asiento nuevo el id (y la marca de vendido)
// del asiento que tenía la misma fila y número. Devuelve los vendidos que ya
// no estarían, para no aplicar un cambio que los borre.
const conIdsAnteriores = (filasNuevas, filasViejas) => {
  const anteriores = new Map();
  filasViejas.forEach((f) => f.asientos.forEach((a) => anteriores.set(claveAsiento(f.nombre, a.etiqueta), a)));
  const usados = new Set();
  const filas = filasNuevas.map((f) => ({
    ...f,
    asientos: f.asientos.map((a) => {
      const clave = claveAsiento(f.nombre, a.etiqueta);
      const viejo = anteriores.get(clave);
      if (!viejo) return a;
      usados.add(clave);
      return { ...a, id: viejo.id, vendido: viejo.vendido };
    }),
  }));
  const perdidos = [];
  anteriores.forEach((a, clave) => {
    if (a.vendido && !usados.has(clave)) perdidos.push(clave.replace("|", " "));
  });
  return { filas, perdidos };
};

// aplicarFilas regenera el bloque de filas del sector con la configuración.
export const aplicarFilas = (sector, config) => {
  const c = { ...FILAS_POR_DEFECTO, ...config };
  const { filas, perdidos } = conIdsAnteriores(generarFilas(c), sector.filas);
  if (perdidos.length > 0) {
    return { sector, error: `Hay asientos vendidos que dejarían de existir: ${perdidos.slice(0, 5).join(", ")}${perdidos.length > 5 ? "…" : ""}.` };
  }
  // el bloque se ubica desde un origen fijo, así una fila movida a mano no
  // corre a las demás. Sin origen guardado (la primera vez, o un sector viejo)
  // el bloque se alinea con una fila que ya estaba; si no hay, ocupa el lugar
  // de las anteriores o va debajo de las mesas.
  let origen = c.origen;
  const comun = !origen && filas.find((f) => f.asientos.length > 0 && sector.filas.some((v) => v.nombre === f.nombre && v.asientos.length > 0));
  if (comun) {
    const vieja = sector.filas.find((v) => v.nombre === comun.nombre).asientos[0];
    const nueva = comun.asientos.find((a) => a.etiqueta === vieja.etiqueta) || comun.asientos[0];
    origen = { x: vieja.x - nueva.x, y: vieja.y - nueva.y };
  }
  if (!origen) {
    const cajaFilas = (lista) => cajaContenido({ filas: lista, mesas: [], decorativos: [] });
    let destino = { x: MARGEN_SECTOR, y: MARGEN_SECTOR };
    if (sector.filas.length > 0) {
      const anterior = cajaFilas(sector.filas);
      destino = { x: anterior.minX, y: anterior.minY };
    } else if (sector.mesas.length > 0) {
      const cajaMesas = cajaContenido({ ...sector, filas: [], decorativos: [] });
      destino = { x: cajaMesas.minX, y: cajaMesas.maxY + c.separacionFilas };
    }
    const nueva = cajaFilas(filas);
    origen = { x: destino.x - nueva.minX, y: destino.y - nueva.minY };
  }
  const ubicadas = moverContenido({ filas, mesas: [], decorativos: [] }, origen.x, origen.y).filas;
  const actualizado = {
    ...sector,
    filas: ubicadas,
    editor: { ...(sector.editor || {}), filas: { ...c, origen } },
  };
  return { sector: ajustarCaja(actualizado), error: null };
};

// indiceEnBloque: el lugar en el bloque de la fila k de la ubicación (difiere
// si hay filas separadas a otra ubicación). Los ajustes se guardan por ese lugar.
export const indiceEnBloque = (sector, k) => {
  const c = configFilas(sector);
  const excluidas = c.excluidas || [];
  let visto = -1;
  for (let f = 0; f < c.filas; f++) {
    if (excluidas.includes(f)) continue;
    visto += 1;
    if (visto === k) return f;
  }
  return k;
};

// ajustarFila cambia una fila sola: dx/dy se suman (moverla), asientos y
// nombre reemplazan los del bloque. Se guarda en la configuración y sobrevive
// a regenerar.
export const ajustarFila = (sector, k, { dx = 0, dy = 0, asientos, nombre, rotacion } = {}) => {
  const c = configFilas(sector);
  const indice = indiceEnBloque(sector, k);
  const ajustes = { ...(c.ajustes || {}) };
  const actual = ajustes[indice] || {};
  const nuevo = {
    dx: Math.round((actual.dx || 0) + dx),
    dy: Math.round((actual.dy || 0) + dy),
    rotacion: rotacion ?? actual.rotacion ?? 0,
    asientos: asientos === undefined ? actual.asientos : asientos === c.asientosPorFila ? undefined : asientos,
    nombre: nombre === undefined ? actual.nombre : nombre.trim() || undefined,
  };
  if (nuevo.nombre) {
    const usado = sector.filas.some((f, i) => i !== k && f.nombre.toLowerCase() === nuevo.nombre.toLowerCase());
    if (usado) return { sector, error: `Ya hay una fila ${nuevo.nombre} en esta ubicación.` };
  }
  Object.keys(nuevo).forEach((k) => !nuevo[k] && delete nuevo[k]);
  if (Object.keys(nuevo).length === 0) delete ajustes[indice];
  else ajustes[indice] = nuevo;
  return aplicarFilas(sector, { ...c, ajustes });
};

// alinearFila devuelve la fila a su lugar en el bloque (conserva su nombre y
// su cantidad de asientos)
export const alinearFila = (sector, k) => {
  const c = configFilas(sector);
  const indice = indiceEnBloque(sector, k);
  const ajustes = { ...(c.ajustes || {}) };
  const { dx: _dx, dy: _dy, rotacion: _r, ...resto } = ajustes[indice] || {};
  if (Object.keys(resto).length > 0) ajustes[indice] = resto;
  else delete ajustes[indice];
  return aplicarFilas(sector, { ...c, ajustes });
};

// nombreDeFilas: "Fila A", "Filas A–C" (seguidas) o "Filas A, C y F"
const nombreDeFilas = (filas, indices) => {
  const ordenados = [...indices].sort((x, y) => x - y);
  const nombres = ordenados.map((k) => filas[k].nombre);
  if (nombres.length === 1) return `Fila ${nombres[0]}`;
  const seguidas = ordenados.every((k, i) => i === 0 || k === ordenados[i - 1] + 1);
  if (seguidas) return `Filas ${nombres[0]}–${nombres[nombres.length - 1]}`;
  return `Filas ${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}`;
};

// separarFilas saca filas de su ubicación y las pone juntas en una ubicación
// nueva que vende la tarifa elegida, en el mismo lugar del plano y sin fondo
// ni nombre (se ve igual). Sus asientos se crean de nuevo en la ubicación
// nueva, así que no separa filas con asientos vendidos.
export const separarFilas = (mapa, claveSector, indices, idtarifa) => {
  const sector = mapa.sectores.find((s) => s.clave === claveSector);
  const ks = [...new Set(indices)].filter((k) => sector?.filas[k]).sort((x, y) => x - y);
  if (!sector || ks.length === 0) return { mapa, error: "No se encontró la fila." };
  if (ks.some((k) => sector.filas[k].asientos.some((a) => a.vendido))) {
    return { mapa, error: "Hay asientos vendidos en esas filas: no se pueden separar." };
  }
  if (ks.length === sector.filas.length) {
    return { mapa, error: "Son todas las filas: cambiá la tarifa de toda la ubicación." };
  }

  // un bloque generado excluye las filas (las demás no se mueven); una parte
  // ya separada (filas sueltas) sólo las saca
  const generado = Boolean(sector.editor?.filas);
  let r;
  if (generado) {
    const c = configFilas(sector);
    r = aplicarFilas(sector, { ...c, excluidas: [...(c.excluidas || []), ...ks.map((k) => indiceEnBloque(sector, k))] });
    if (r.error) return { mapa, error: r.error };
  } else {
    const origenes = sector.editor?.filasDeOrigen;
    const editor = origenes ? { ...sector.editor, filasDeOrigen: origenes.filter((_, k) => !ks.includes(k)) } : sector.editor;
    r = { sector: ajustarCaja({ ...sector, filas: sector.filas.filter((_, k) => !ks.includes(k)), editor }) };
  }

  // la parte separada recuerda de qué ubicación salió y qué filas del bloque
  // eran, para poder volver
  const vista = { ocultarFondo: true, ocultarNombre: true, pieza: sector.editor?.pieza || sector.clave };
  if (generado && !sector.editor?.pieza) vista.filasDeOrigen = ks.map((k) => indiceEnBloque(sector, k));
  else if (sector.editor?.filasDeOrigen) vista.filasDeOrigen = ks.map((k) => sector.editor.filasDeOrigen[k]);
  if (sector.editor?.numeracion) vista.numeracion = sector.editor.numeracion;
  const nueva = ajustarCaja({
    ...crearSectorGeneral(mapa, { x: sector.x, y: sector.y }),
    nombre: nombreUnico(mapa, `${sector.nombre} · ${nombreDeFilas(sector.filas, ks)}`),
    color: sector.color,
    rotacion: sector.rotacion || 0,
    limite_persona: sector.limite_persona,
    capacidad: 0,
    filas: ks.map((k) => ({ ...sector.filas[k], asientos: sector.filas[k].asientos.map(({ id, vendido, ...a }) => a) })),
    editor: vista,
  });
  const conNueva = {
    ...mapa,
    sectores: [...mapa.sectores.map((s) => (s.clave === claveSector ? r.sector : s)), nueva],
  };
  const resultado = idtarifa ? moverTarifa(conNueva, idtarifa, nueva.clave) : conNueva;
  return { mapa: resultado, clave: nueva.clave, error: null };
};

export const separarFila = (mapa, claveSector, k, idtarifa) => separarFilas(mapa, claveSector, [k], idtarifa);

// separarMesas: lo mismo que separarFilas, con mesas. Las mesas y sus sillas
// se crean de nuevo, así que no separa mesas con algo vendido.
export const separarMesas = (mapa, claveSector, claves, idtarifa) => {
  const sector = mapa.sectores.find((s) => s.clave === claveSector);
  const elegidas = sector ? sector.mesas.filter((m) => claves.includes(m.clave)) : [];
  if (elegidas.length === 0) return { mapa, error: "No se encontró la mesa." };
  if (elegidas.some((m) => m.vendida || m.sillas.some((x) => x.vendido))) {
    return { mapa, error: "Hay mesas con algo vendido: no se pueden separar." };
  }
  if (elegidas.length === sector.mesas.length && sector.filas.length === 0) {
    return { mapa, error: "Son todas las mesas: cambiá la tarifa de toda la ubicación." };
  }
  const restantes = sector.mesas.filter((m) => !claves.includes(m.clave));
  const original = ajustarCaja({ ...sector, mesas: restantes });

  const vista = { ocultarFondo: true, ocultarNombre: true, pieza: sector.editor?.pieza || sector.clave };
  const nombres = elegidas.map((m) => m.nombre);
  const titulo = nombres.length === 1 ? nombres[0] : `${nombres.length} mesas`;
  const nueva = ajustarCaja({
    ...crearSectorGeneral(mapa, { x: sector.x, y: sector.y }),
    nombre: nombreUnico(mapa, `${sector.nombre} · ${titulo}`),
    rotacion: sector.rotacion || 0,
    limite_persona: sector.limite_persona,
    capacidad: 0,
    mesas: elegidas.map(({ id, vendida, ...m }) => ({ ...m, sillas: m.sillas.map(({ id: _id, vendido, ...x }) => x) })),
    editor: vista,
  });
  const conNueva = {
    ...mapa,
    sectores: [...mapa.sectores.map((s) => (s.clave === claveSector ? original : s)), nueva],
  };
  const resultado = idtarifa ? moverTarifa(conNueva, idtarifa, nueva.clave) : conNueva;
  return { mapa: resultado, clave: nueva.clave, error: null };
};

// configFilas: la configuración guardada o, en un sector viejo, la que se
// deduce de sus filas.
export const configFilas = (sector) => {
  if (sector.editor?.filas) return { ...FILAS_POR_DEFECTO, ...sector.editor.filas };
  const filas = sector.filas.filter((f) => f.nombre !== "");
  if (filas.length === 0) return { ...FILAS_POR_DEFECTO };
  const primera = filas[0];
  const numerica = /^\d+$/.test(primera.nombre);
  const primerAsiento = Math.min(...primera.asientos.map((a) => parseInt(a.etiqueta, 10)).filter((n) => !Number.isNaN(n)));
  return {
    ...FILAS_POR_DEFECTO,
    filas: filas.length,
    asientosPorFila: Math.max(...filas.map((f) => f.asientos.length)),
    curva: primera.curva || 0,
    separacion: primera.separacion || FILAS_POR_DEFECTO.separacion,
    nombres: numerica ? "numeros" : "letras",
    primeraFila: primera.nombre,
    primerAsiento: Number.isFinite(primerAsiento) ? primerAsiento : 1,
  };
};

const tamanoMesa = (forma, sillas) =>
  forma === "rectangular"
    ? { ancho: Math.max(80, Math.ceil(sillas / 2) * (2 * RADIO_ASIENTO + 10) + 20), alto: 50 }
    : { ancho: Math.max(60, 40 + sillas * 6), alto: Math.max(60, 40 + sillas * 6) };

const sillasNumeradas = (cantidad, primera = 1, anteriores = []) =>
  Array.from({ length: cantidad }, (_, i) => {
    const etiqueta = String(primera + i);
    const vieja = anteriores.find((s) => s.etiqueta === etiqueta);
    return vieja ? { ...vieja } : { etiqueta, x: 0, y: 0, rotacion: 0 };
  });

const nombreMesaLibre = (sector) => {
  const usados = new Set(sector.mesas.map((m) => m.nombre.toLowerCase()));
  for (let n = sector.mesas.length + 1; ; n++) {
    if (!usados.has(`mesa ${n}`)) return `Mesa ${n}`;
  }
};

// agregarMesa suma una mesa al sector, a la derecha de lo que ya tiene.
export const agregarMesa = (sector, { forma = "redonda", sillas = 6 } = {}) => {
  const tamano = tamanoMesa(forma, sillas);
  const caja = cajaContenido(sector);
  const separacion = 3 * RADIO_ASIENTO + 20;
  const mesa = {
    clave: nuevaClave("m"),
    nombre: nombreMesaLibre(sector),
    forma,
    x: caja ? Math.round(caja.maxX + separacion) : MARGEN_SECTOR + 2 * RADIO_ASIENTO,
    y: caja ? Math.round(caja.minY + 2 * RADIO_ASIENTO + 4) : MARGEN_SECTOR + 2 * RADIO_ASIENTO,
    ...tamano,
    rotacion: 0,
    sillas: sillasNumeradas(sillas),
  };
  return { sector: ajustarCaja({ ...sector, mesas: [...sector.mesas, mesa] }), clave: mesa.clave };
};

// actualizarMesa cambia nombre, forma o cantidad de sillas. Una mesa vendida
// no cambia de sillas, y nunca se saca una silla vendida.
export const actualizarMesa = (sector, clave, cambios) => {
  const mesa = sector.mesas.find((m) => m.clave === clave);
  if (!mesa) return { sector, error: null };
  const cantidad = cambios.sillas ?? mesa.sillas.length;
  const forma = cambios.forma ?? mesa.forma;
  if (cantidad !== mesa.sillas.length) {
    if (mesa.vendida) return { sector, error: "La mesa está vendida entera: no se le pueden cambiar las sillas." };
    const sacadas = mesa.sillas.slice(cantidad);
    if (sacadas.some((s) => s.vendido)) return { sector, error: "No se puede sacar una silla vendida." };
  }
  const nueva = {
    ...mesa,
    ...(cambios.nombre !== undefined ? { nombre: cambios.nombre } : {}),
    ...(cambios.rotacion !== undefined ? { rotacion: cambios.rotacion } : {}),
    forma,
    sillas: cantidad !== mesa.sillas.length ? sillasNumeradas(cantidad, 1, mesa.sillas) : mesa.sillas,
  };
  if (cantidad !== mesa.sillas.length || forma !== mesa.forma) {
    Object.assign(nueva, tamanoMesa(forma, cantidad));
  }
  return { sector: ajustarCaja({ ...sector, mesas: sector.mesas.map((m) => (m.clave === clave ? nueva : m)) }), error: null };
};

export const moverMesa = (sector, clave, { x, y }) =>
  ajustarCaja({ ...sector, mesas: sector.mesas.map((m) => (m.clave === clave ? { ...m, x, y } : m)) });

export const duplicarMesa = (sector, clave) => {
  const mesa = sector.mesas.find((m) => m.clave === clave);
  if (!mesa) return { sector, clave: null };
  const { id, vendida, ...resto } = mesa;
  const copia = {
    ...resto,
    clave: nuevaClave("m"),
    nombre: nombreMesaLibre(sector),
    x: mesa.x + mesa.ancho + 3 * RADIO_ASIENTO + 20,
    sillas: mesa.sillas.map(({ id: _id, vendido, ...s }) => s),
  };
  return { sector: ajustarCaja({ ...sector, mesas: [...sector.mesas, copia] }), clave: copia.clave };
};

export const quitarMesa = (sector, clave) => {
  const mesa = sector.mesas.find((m) => m.clave === clave);
  if (mesa && (mesa.vendida || mesa.sillas.some((s) => s.vendido))) {
    return { sector, error: "La mesa tiene sillas vendidas: no se puede borrar." };
  }
  return { sector: ajustarCaja({ ...sector, mesas: sector.mesas.filter((m) => m.clave !== clave) }), error: null };
};

// quitarFilas saca todas las filas. Si no quedan mesas, la ubicación vuelve a
// ser general con la capacidad que tenían sus asientos.
export const quitarFilas = (sector) => {
  if (sector.filas.some((f) => f.asientos.some((a) => a.vendido))) {
    return { sector, error: "Hay asientos vendidos en las filas: no se pueden quitar." };
  }
  const { filas: _config, ...editor } = sector.editor || {};
  const sinFilas = { ...sector, filas: [], editor };
  if (sector.mesas.length > 0) return { sector: ajustarCaja(sinFilas), error: null };
  const asientos = sector.filas.reduce((total, f) => total + f.asientos.length, 0);
  return { sector: { ...sinFilas, capacidad: Number(sector.capacidad) || asientos }, error: null };
};

export const TIPO_UBICACION = { GENERAL: "general", FILAS: "filas", MESAS: "mesas", MIXTA: "mixta" };

export const tipoDe = (sector) => {
  if (sector.filas.length > 0 && sector.mesas.length > 0) return TIPO_UBICACION.MIXTA;
  if (sector.filas.length > 0) return TIPO_UBICACION.FILAS;
  if (sector.mesas.length > 0) return TIPO_UBICACION.MESAS;
  return TIPO_UBICACION.GENERAL;
};

const quitarMesas = (sector) => {
  if (sector.mesas.some((m) => m.vendida || m.sillas.some((s) => s.vendido))) {
    return { sector, error: "Hay mesas con sillas vendidas: no se pueden quitar." };
  }
  const sinMesas = { ...sector, mesas: [] };
  return { sector: sinMesas.filas.length > 0 ? ajustarCaja(sinMesas) : sinMesas, error: null };
};

// cambiarTipo pasa la ubicación a general, filas o mesas. Conserva nombre y
// tarifas, nunca borra lugares vendidos y, si ya vendió, no cambia entre
// general y con lugares (las entradas vendidas no tendrían lugar).
export const cambiarTipo = (sector, tipo) => {
  const actual = tipoDe(sector);
  if (tipo === actual) return { sector, error: null };
  const pasaAGeneral = tipo === TIPO_UBICACION.GENERAL;
  if (sector.con_ventas && pasaAGeneral !== (actual === TIPO_UBICACION.GENERAL)) {
    return { sector, error: "Ya tiene ventas: no puede pasar de general a con lugares ni al revés." };
  }
  const enPlano = (s) => (s.en_mapa ? s : { ...s, en_mapa: true });
  const lugares = sector.filas.reduce((t, f) => t + f.asientos.length, 0) + sector.mesas.reduce((t, m) => t + m.sillas.length, 0);

  let r = { sector, error: null };
  if (tipo !== TIPO_UBICACION.MESAS && sector.mesas.length > 0) r = quitarMesas(r.sector);
  if (r.error) return r;
  if (tipo !== TIPO_UBICACION.FILAS && r.sector.filas.length > 0) r = quitarFilas(r.sector);
  if (r.error) return r;

  if (pasaAGeneral) return { sector: { ...r.sector, capacidad: Number(sector.capacidad) || lugares }, error: null };
  if (tipo === TIPO_UBICACION.FILAS) return aplicarFilas(enPlano(r.sector), configFilas(r.sector));
  return agregarMesa(enPlano(r.sector), { forma: "redonda", sillas: 6 });
};

// acomodarMesas reparte las mesas en renglones dentro de un ancho, en el orden
// en que se crearon (como el texto en un lienzo). No crea ni borra mesas.
export const acomodarMesas = (sector, anchoDisponible) => {
  if (sector.mesas.length === 0) return { sector, error: null };
  const separacion = 2 * RADIO_ASIENTO + 12;
  const limite = anchoDisponible - MARGEN_SECTOR;
  let x = MARGEN_SECTOR;
  let y = MARGEN_SECTOR;
  let altoRenglon = 0;
  const mesas = sector.mesas.map((m) => {
    const caja = cajaContenido({ filas: [], decorativos: [], mesas: [m] });
    const ancho = caja.maxX - caja.minX;
    const alto = caja.maxY - caja.minY;
    if (x > MARGEN_SECTOR && x + ancho > limite) {
      x = MARGEN_SECTOR;
      y += altoRenglon + separacion;
      altoRenglon = 0;
    }
    const ubicada = { ...m, x: Math.round(m.x + x - caja.minX), y: Math.round(m.y + y - caja.minY) };
    x += ancho + separacion;
    altoRenglon = Math.max(altoRenglon, alto);
    return ubicada;
  });
  return { sector: ajustarCaja({ ...sector, mesas }), error: null };
};

// Sectores nuevos con lugares: arrancan como uno general y se les generan
// las filas o la primera mesa.
export const crearSectorFilas = (mapa, { x, y }, config = {}) => {
  const base = { ...crearSectorGeneral(mapa, { x, y }), nombre: nombreUnico(mapa, "Platea"), capacidad: 0 };
  return aplicarFilas(base, config).sector;
};

export const crearSectorMesas = (mapa, { x, y }, forma) => {
  const base = { ...crearSectorGeneral(mapa, { x, y }), nombre: nombreUnico(mapa, "Mesas"), capacidad: 0 };
  return agregarMesa(base, { forma, sillas: forma === "rectangular" ? 8 : 6 }).sector;
};
